// CR-007 §4.6 / DESIGN.md §4.3: the one table that decides which sign-in page a visitor who hits
// a protected route with no session is sent to. It maps a route **prefix**, never a role — it
// reads no cookie, calls no API, and decides no authorization question. Every consumer that used
// to write the literal `/masuk` calls this instead, so a new protected route group that forgets to
// register here is a one-line fix instead of a silent inheritance of the investor door.
export const INVESTOR_DOOR = '/masuk';
export const STAFF_DOOR = '/masuk/staf';

/**
 * Protected prefixes that use the staff door, `/masuk/staf` — the three staff-only route groups
 * (DESIGN.md §4.3). Every other protected prefix, `/akun` included (it is shared by all five
 * roles, and its cross-link is the staff member's one click from there — CR-007 §4.5), uses the
 * investor door. This list is the single source of truth `doors.test.ts` and
 * `middleware.test.ts`'s completeness check both read, so the two can never drift apart.
 */
export const STAFF_DOOR_PREFIXES: readonly string[] = ['/ppa', '/ops', '/admin'];

/**
 * Every protected route prefix this app knows, together with the door it resolves to.
 * `middleware.test.ts` asserts that this list accounts for every protected route group under
 * `src/app/`, so a future route group that is not added here fails `make verify-frontend` instead
 * of silently falling through to the investor door (CR-007 §7 risk 4).
 */
export const PROTECTED_PREFIX_DOORS: readonly { prefix: string; door: string }[] = [
  { prefix: '/ppa', door: STAFF_DOOR },
  { prefix: '/ops', door: STAFF_DOOR },
  { prefix: '/admin', door: STAFF_DOOR },
  { prefix: '/akun', door: INVESTOR_DOOR },
  { prefix: '/rumah', door: INVESTOR_DOOR },
  { prefix: '/kyc', door: INVESTOR_DOOR },
  { prefix: '/investasi', door: INVESTOR_DOOR },
  { prefix: '/portofolio', door: INVESTOR_DOOR },
  { prefix: '/distribusi', door: INVESTOR_DOOR },
  { prefix: '/saldo', door: INVESTOR_DOOR },
  { prefix: '/rekening', door: INVESTOR_DOOR },
  { prefix: '/pajak', door: INVESTOR_DOOR },
  { prefix: '/edukasi', door: INVESTOR_DOOR },
  { prefix: '/tutup-akun', door: INVESTOR_DOOR },
];

/**
 * Which sign-in page a visitor at `pathname` with no session is sent to. Pure: no cookie read, no
 * API call, no role decided — it maps a route **prefix** to a *public* page, so choosing wrongly
 * can only send a visitor to the other public door, never past an authorization check (CR-007
 * §4.6, DESIGN.md §4.3).
 */
export function signInPathFor(pathname: string): string {
  const isStaffPath = STAFF_DOOR_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
  return isStaffPath ? STAFF_DOOR : INVESTOR_DOOR;
}
